import { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Trans, useTranslation } from 'react-i18next'
import { contactApi } from '../api/client'
import { useAuth } from '../context/AuthContext'
import { INQUIRY_CATEGORY_OPTIONS, type ContactMessage, type InquiryCategory } from '../types'

const MAX_ATTACHMENT_BYTES = 20 * 1024 * 1024 // 20MB — 백엔드 ContactService 제한과 동일
const MAX_CONTENT_LENGTH = 2000

function InquiryList() {
  const { t } = useTranslation()
  const [messages, setMessages] = useState<ContactMessage[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [downloadingId, setDownloadingId] = useState<number | null>(null)

  useEffect(() => {
    (async () => {
      setLoading(true)
      try {
        setMessages(await contactApi.list())
      } catch {
        setError(t('contact.list.loadFailed'))
      } finally {
        setLoading(false)
      }
    })()
  }, [])

  const handleDownload = async (m: ContactMessage) => {
    if (!m.attachmentFileName) return
    setDownloadingId(m.id)
    try {
      await contactApi.downloadAttachment(m.id, m.attachmentFileName)
    } catch {
      setError(t('contact.list.downloadFailed'))
    } finally {
      setDownloadingId(null)
    }
  }

  if (loading) return <div className="text-center text-sm text-slate-400 py-10">{t('common.loading')}</div>

  return (
    <div className="space-y-3">
      {error && <div className="bg-red-50 border border-red-200 text-red-700 px-3 py-2.5 rounded text-sm">{error}</div>}
      {messages.length === 0 ? (
        <div className="text-center text-sm text-slate-400 py-10">{t('contact.list.empty')}</div>
      ) : (
        <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-50">
              <tr>
                <th className="px-4 py-2 text-left font-semibold text-slate-600">{t('contact.list.colTitle')}</th>
                <th className="px-4 py-2 text-left font-semibold text-slate-600">{t('contact.list.colAuthor')}</th>
                <th className="px-4 py-2 text-left font-semibold text-slate-600">{t('contact.list.colReplyEmail')}</th>
                <th className="px-4 py-2 text-left font-semibold text-slate-600">{t('contact.list.colCreated')}</th>
                <th className="px-4 py-2 text-left font-semibold text-slate-600">{t('contact.list.colAttachment')}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {messages.map(m => (
                <tr key={m.id} className="hover:bg-slate-50">
                  <td className="px-4 py-2.5 text-slate-800 align-top">
                    <span className="text-[10px] font-bold text-primary-700 bg-primary-50 px-1.5 py-0.5 rounded mr-1.5 align-middle">{t(`contact.category.${m.category}`)}</span>
                    {m.title}
                    {m.content && <p className="text-xs text-slate-400 mt-1 whitespace-pre-wrap">{m.content}</p>}
                  </td>
                  <td className="px-4 py-2.5 text-slate-500 align-top">
                    {m.authorName}
                    {m.authorEmail && <p className="text-[11px] text-slate-400">{m.authorEmail}</p>}
                  </td>
                  <td className="px-4 py-2.5 text-slate-500 align-top">{m.contactEmail || <span className="text-slate-300">-</span>}</td>
                  <td className="px-4 py-2.5 text-slate-400 text-xs align-top">{m.createdAt.slice(0, 10)}</td>
                  <td className="px-4 py-2.5 align-top">
                    {m.hasAttachment ? (
                      <button
                        onClick={() => handleDownload(m)}
                        disabled={downloadingId === m.id}
                        className="text-xs text-primary-600 font-semibold hover:underline disabled:opacity-50"
                      >
                        {downloadingId === m.id ? t('contact.list.downloading') : m.attachmentFileName}
                      </button>
                    ) : (
                      <span className="text-xs text-slate-300">-</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}

export default function ContactPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { user } = useAuth()
  const isAdmin = !!user?.admin
  const fileRef = useRef<HTMLInputElement>(null)

  const [mode, setMode] = useState<'write' | 'list'>('write')
  const [title, setTitle] = useState('')
  const [content, setContent] = useState('')
  const [category, setCategory] = useState<InquiryCategory | ''>('')
  const [contactEmail, setContactEmail] = useState(user?.email ?? '')
  const [file, setFile] = useState<File | null>(null)
  const [isDragging, setIsDragging] = useState(false)
  const [fileError, setFileError] = useState('')
  const [error, setError] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [done, setDone] = useState(false)

  const canSubmit = title.trim().length > 0 && content.trim().length > 0 && category !== '' && !submitting

  const pickFile = (f: File) => {
    if (f.size > MAX_ATTACHMENT_BYTES) {
      setFileError(t('contact.form.attachmentTooLarge'))
      return
    }
    setFile(f)
    setFileError('')
  }

  const handleSubmit = async () => {
    if (!canSubmit) return
    setError('')
    setSubmitting(true)
    try {
      const fd = new FormData()
      fd.append('title', title.trim())
      fd.append('content', content.trim())
      fd.append('category', category)
      if (contactEmail.trim()) fd.append('contactEmail', contactEmail.trim())
      if (file) fd.append('file', file)
      await contactApi.submit(fd)
      setDone(true)
    } catch (e: unknown) {
      const err = e as { response?: { data?: { error?: string } } }
      setError(err.response?.data?.error ?? t('contact.form.submitFailed'))
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="h-12 bg-slate-900 flex items-center gap-3 px-4 shrink-0">
        <button
          onClick={() => navigate('/dashboard')}
          className="text-xs text-slate-400 hover:text-white border border-slate-600 hover:border-slate-400 rounded px-3 py-1 transition-colors flex items-center gap-1.5"
        >
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="15 18 9 12 15 6" />
          </svg>
          {t('support.back')}
        </button>
        <h1 className="text-white text-sm font-semibold ml-1">{t('contact.form.pageTitle')}</h1>
      </header>

      <main className={mode === 'list' ? 'max-w-3xl mx-auto px-6 py-10 space-y-4' : 'max-w-4xl mx-auto px-6 py-10 space-y-4'}>
        {isAdmin && (
          <div className="flex gap-3">
            <button
              onClick={() => setMode('write')}
              className={mode === 'write' ? 'text-xs font-semibold text-primary-600 border-b-2 border-primary-600 pb-1' : 'text-xs font-semibold text-slate-400 hover:text-slate-600 pb-1'}
            >
              {t('contact.form.tabContact')}
            </button>
            <button
              onClick={() => setMode('list')}
              className={mode === 'list' ? 'text-xs font-semibold text-primary-600 border-b-2 border-primary-600 pb-1' : 'text-xs font-semibold text-slate-400 hover:text-slate-600 pb-1'}
            >
              {t('contact.form.tabInbox')}
            </button>
          </div>
        )}

        {mode === 'list' ? <InquiryList /> : done ? (
          <div className="max-w-xl mx-auto bg-white border border-slate-200 rounded-xl p-8 text-center space-y-3">
            <div className="w-12 h-12 rounded-full bg-green-100 text-green-600 mx-auto flex items-center justify-center">
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="20 6 9 17 4 12" />
              </svg>
            </div>
            <p className="text-sm font-semibold text-slate-800">{t('contact.form.doneTitle')}</p>
            <p className="text-xs text-slate-500">{t('contact.form.doneDesc')}</p>
            <button
              onClick={() => navigate('/dashboard')}
              className="mt-2 px-4 py-2 rounded-md text-sm font-semibold text-white bg-primary-600 hover:bg-primary-700 transition-colors"
            >
              {t('support.back')}
            </button>
          </div>
        ) : (
          <>
            <p className="text-xs text-slate-500 -mt-1">{t('contact.form.sla')}</p>

            <div className="grid grid-cols-1 lg:grid-cols-[1.6fr_1fr] gap-4 items-start">
              <div className="bg-white border border-slate-200 rounded-xl p-6 space-y-4">
                {error && <div className="bg-red-50 border border-red-200 text-red-700 px-3 py-2.5 rounded text-sm">{error}</div>}

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label className="block text-[11px] font-semibold text-slate-600 mb-1">{t('contact.form.type')} <span className="text-red-500">*</span></label>
                    <select value={category} onChange={e => setCategory(e.target.value as InquiryCategory)}
                      disabled={submitting} className="input-field">
                      <option value="" disabled>{t('contact.form.select')}</option>
                      {INQUIRY_CATEGORY_OPTIONS.map(o => (
                        <option key={o.value} value={o.value}>{t(o.labelKey)}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-[11px] font-semibold text-slate-600 mb-1">{t('contact.form.replyEmail')}</label>
                    <input type="email" value={contactEmail} onChange={e => setContactEmail(e.target.value)} placeholder="name@company.com"
                      disabled={submitting} className="input-field" />
                  </div>
                </div>

                <div>
                  <label className="block text-[11px] font-semibold text-slate-600 mb-1">{t('contact.form.subject')} <span className="text-red-500">*</span></label>
                  <input value={title} onChange={e => setTitle(e.target.value)} placeholder={t('contact.form.subjectPlaceholder')}
                    disabled={submitting} className="input-field" />
                </div>

                <div>
                  <label className="block text-[11px] font-semibold text-slate-600 mb-1">{t('contact.form.message')} <span className="text-red-500">*</span></label>
                  <textarea value={content} onChange={e => setContent(e.target.value.slice(0, MAX_CONTENT_LENGTH))} rows={7}
                    placeholder={t('contact.form.messagePlaceholder')} disabled={submitting} className="input-field resize-none" />
                  <p className="text-[11px] text-slate-400 mt-1 text-right">{content.length} / {MAX_CONTENT_LENGTH}</p>
                </div>

                <div>
                  <label className="block text-[11px] font-semibold text-slate-600 mb-1">{t('contact.form.attachment')}</label>
                  <div
                    onDragOver={e => { e.preventDefault(); setIsDragging(true) }}
                    onDragLeave={() => setIsDragging(false)}
                    onDrop={e => { e.preventDefault(); setIsDragging(false); const f = e.dataTransfer.files[0]; if (f) pickFile(f) }}
                    onClick={() => !submitting && fileRef.current?.click()}
                    className={[
                      'border-2 border-dashed rounded-lg p-5 text-center transition-colors',
                      isDragging ? 'border-primary-400 bg-primary-50' : 'border-slate-200 bg-slate-50 hover:border-slate-300',
                      submitting ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer',
                    ].join(' ')}
                  >
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className="mx-auto text-slate-400">
                      <path d="M21.44 11.05l-9.19 9.19a6 6 0 01-8.49-8.49l9.19-9.19a4 4 0 015.66 5.66l-9.2 9.19a2 2 0 01-2.83-2.83l8.49-8.48" />
                    </svg>
                    {file ? (
                      <p className="text-sm font-semibold text-slate-700 mt-1">{file.name} <span className="text-slate-400 font-normal text-xs">({(file.size / 1024 / 1024).toFixed(2)} MB)</span></p>
                    ) : (
                      <p className="text-sm text-slate-500 mt-1"><Trans i18nKey="contact.form.dropHint" components={{ hl: <span className="text-primary-600 font-semibold" /> }} /></p>
                    )}
                    <input ref={fileRef} type="file" className="sr-only"
                      onChange={e => { const f = e.target.files?.[0]; if (f) pickFile(f); e.target.value = '' }}
                      disabled={submitting} />
                  </div>
                  {fileError && <p className="text-xs text-red-600 mt-1">{fileError}</p>}
                </div>

                <div className="flex justify-end gap-2 pt-2">
                  <button onClick={() => navigate('/dashboard')} disabled={submitting} className="btn-secondary disabled:opacity-50">{t('common.cancel')}</button>
                  <button onClick={handleSubmit} disabled={!canSubmit}
                    className="px-5 py-2 rounded-md text-sm font-semibold text-white bg-primary-600 hover:bg-primary-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
                    {submitting ? t('contact.form.submitting') : t('contact.form.submit')}
                  </button>
                </div>
              </div>

              <div className="flex flex-col gap-3">
                <div className="bg-white border border-slate-200 rounded-xl p-4">
                  <div className="flex gap-2.5">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className="text-slate-400 shrink-0 mt-0.5">
                      <path d="M4 4h16v16H4z" />
                      <path d="M22 6l-10 7L2 6" />
                    </svg>
                    <div>
                      <p className="text-xs font-semibold text-slate-700">{t('contact.form.sideTitle')}</p>
                      <p className="text-[11px] text-slate-500 mt-0.5 leading-relaxed">{t('contact.form.sideDesc')}</p>
                    </div>
                  </div>
                </div>

                <div className="bg-primary-50 rounded-xl p-4">
                  <p className="text-xs font-semibold text-primary-700 mb-0.5">{t('contact.form.faqTitle')}</p>
                  <p className="text-[11px] text-primary-600 leading-relaxed mb-2.5">{t('contact.form.faqDesc')}</p>
                  <button onClick={() => navigate('/qna')} className="text-[11px] font-semibold text-primary-700 hover:underline">
                    {t('contact.form.faqLink')}
                  </button>
                </div>
              </div>
            </div>
          </>
        )}
      </main>
    </div>
  )
}
